import React, { useState, useEffect, useRef } from "react";
import { Chip, Tooltip } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';

const TagReviewModal = ({
  open,
  onClose,
  onProceed,
  jdAnalysis,
  roleTitle,
  isEditMode = false,
  jobDescriptionUrl = null,
  amNote = null,
  showGenderFilter = false,
  showStreamFilter = true, // New prop to control stream filter visibility
  roleData = null, // New prop to pass role data for stream checking
  defaultGender = "any", // Default gender filter value
  defaultSelectedStream = "", // Default stream value
  defaultRequireNoVisa = false, // Default visa filter value
}) => {
  // Combine must-have and nice-to-have tags into a single array
  const [requirementTags, setRequirementTags] = useState([]);
  
  // Keep the experience and industry tags (hidden but used in backend)
  const [experienceTags, setExperienceTags] = useState([]);
  const [industryTags, setIndustryTags] = useState([]);
  
  // State for new tag input
  const [newRequirementTag, setNewRequirementTag] = useState("");
  const textareaRef = useRef(null);
  
  // State for gender filter
  const [gender, setGender] = useState(defaultGender); // 'any', 'Man', 'Woman'
  
  // State for stream selection (only when role doesn't have a stream)
  const [selectedStream, setSelectedStream] = useState(defaultSelectedStream);
  
  // State for visa filter
  const [requireNoVisa, setRequireNoVisa] = useState(defaultRequireNoVisa);
  
  // Track active editing
  const [isEditing, setIsEditing] = useState(true); // Start in editing mode by default
  
  // Animation states
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newRequirementTag]);

  // Available stream options
  const streamOptions = [
    { value: "Generalist", label: "Generalist" },
    { value: "Sales", label: "Sales" },
    { value: "Software Engineering", label: "Software Engineering" },
    { value: "Marketing", label: "Marketing" }
  ];

  // Check if role has a stream - role doesn't have a stream if it's "Not specified" or empty
  const getRoleStream = () => {
    if (!roleData || !roleData["Stream"]) return "";
    
    // Handle case where Stream might be an array (get first element) or other types
    let streamValue = roleData["Stream"];
    if (Array.isArray(streamValue)) {
      streamValue = streamValue.length > 0 ? streamValue[0] : "";
    }
    
    // Convert to string to be safe
    return String(streamValue || "");
  };
  
  const roleStreamValue = getRoleStream();
  const roleHasStream = roleStreamValue && roleStreamValue !== "Not specified" && roleStreamValue.trim() !== "";
  const shouldShowStreamSelector = showStreamFilter && !roleHasStream;
  
  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose('cancel');
    }
  };
  
  // Clear input field
  const clearInput = () => {
    setNewRequirementTag("");
  };
  
  // Parse tags from comma-separated strings
  useEffect(() => {
    if (jdAnalysis) {
      // Simulate loading for a smoother UX
      setIsLoading(true);
      
      setTimeout(() => {
        // Combine must-have and nice-to-have into a single requirements list
        // Handle both array format (new) and comma-separated string format (old)
        const mustHaves = jdAnalysis.must_have ? 
          (Array.isArray(jdAnalysis.must_have) 
            ? jdAnalysis.must_have.map(tag => ({
                text: tag.trim(),
                type: 'must-have'
              })).filter(tag => tag.text)
            : jdAnalysis.must_have.split(',').map(tag => ({
                text: tag.trim(),
                type: 'must-have'
              })).filter(tag => tag.text)
          ) : [];
        
        const niceToHaves = jdAnalysis.nice_to_have ? 
          (Array.isArray(jdAnalysis.nice_to_have)
            ? jdAnalysis.nice_to_have.map(tag => ({
                text: tag.trim(),
                type: 'nice-to-have'
              })).filter(tag => tag.text)
            : jdAnalysis.nice_to_have.split(',').map(tag => ({
                text: tag.trim(),
                type: 'nice-to-have'
              })).filter(tag => tag.text)
          ) : [];
        
        // Always add the industry as a nice-to-have tag
        const industryFocusList = jdAnalysis.industryFocus ? jdAnalysis.industryFocus.split(',').map(industry => industry.trim()).filter(industry => industry) : [];
        
        // Create industry nice-to-have tags with the specified format
        const industryNiceToHaves = [];
        
        // Check for duplicates before adding industry tags
        industryFocusList.forEach(industry => {
          // Skip empty industry values
          if (!industry) return;
          
          // Create the industry tag with the specified format
          const industryTag = `interest or experience in ${industry} industry`;
          
          // Check if this industry is already mentioned in the nice-to-have tags
          const isDuplicate = niceToHaves.some(tag => 
            tag.text.toLowerCase().includes(industry.toLowerCase()) && 
            (tag.text.toLowerCase().includes('industry') || 
             tag.text.toLowerCase().includes('experience'))
          );
          
          // Only add if not a duplicate
          if (!isDuplicate) {
            industryNiceToHaves.push({
              text: industryTag,
              type: 'nice-to-have'
            });
          }
        });
        
        // Log the industry tags that were added
        if (industryNiceToHaves.length > 0) {
          console.log(`Added ${industryNiceToHaves.length} industry tags as nice-to-have requirements`);
        }
        
        // Combine all tags
        setRequirementTags([...mustHaves, ...niceToHaves, ...industryNiceToHaves]);
        
        // Keep experience and industry for backend
        // Handle both object format (new) and comma-separated string format (old) for experience
        let experienceTags = [];
        if (jdAnalysis.experience) {
          if (typeof jdAnalysis.experience === 'object' && jdAnalysis.experience.domains) {
            // New format: object with domains array
            experienceTags = jdAnalysis.experience.domains.map(tag => tag.trim()).filter(tag => tag);
          } else if (typeof jdAnalysis.experience === 'string') {
            // Old format: comma-separated string
            experienceTags = jdAnalysis.experience.split(',').map(tag => tag.trim()).filter(tag => tag);
          }
        }
        setExperienceTags(experienceTags);
        
        setIndustryTags(jdAnalysis.industryFocus ? jdAnalysis.industryFocus.split(',').map(tag => tag.trim()).filter(tag => tag) : []);
        
        setIsLoading(false);
        setTimeout(() => setIsReady(true), 300);
      }, 600); // Reduced loading time
    }
  }, [jdAnalysis]);

  // Reset filters to default values when modal opens or defaults change
  useEffect(() => {
    if (open) {
      setGender(defaultGender);
      setSelectedStream(defaultSelectedStream);
      setRequireNoVisa(defaultRequireNoVisa);
    }
  }, [open, defaultGender, defaultSelectedStream, defaultRequireNoVisa]);

  // Add a new tag to requirements
  const addTag = (newTag) => {
    if (!newTag.trim()) return;
    setRequirementTags([...requirementTags, { text: newTag.trim(), type: 'must-have' }]);
    setNewRequirementTag("");
  };

  // Remove a tag from requirements
  const removeTag = (indexToRemove) => {
    setRequirementTags(requirementTags.filter((_, index) => index !== indexToRemove));
  };

  // Toggle tag type between must-have and nice-to-have
  const toggleTagType = (index) => {
    const updatedTags = [...requirementTags];
    updatedTags[index].type = updatedTags[index].type === 'must-have' ? 'nice-to-have' : 'must-have';
    setRequirementTags(updatedTags);
  };

  // Convert tag arrays back to comma-separated strings
  const getUpdatedAnalysis = () => {
    return {
      must_have: requirementTags.filter(tag => tag.type === 'must-have').map(tag => tag.text).join(', '),
      nice_to_have: requirementTags.filter(tag => tag.type === 'nice-to-have').map(tag => tag.text).join(', '),
      experience: experienceTags.join(', '),
      industryFocus: industryTags.join(', ')
    };
  };

  // Handle the proceed button click
  const handleProceed = () => {
    // Make a local copy of the updated analysis
    const updatedAnalysis = getUpdatedAnalysis();
    
    // Check if this is a single sentence search (alternative path)
    const isSingleSentenceSearch = requirementTags.length === 1 && 
      requirementTags[0].text.split(' ').length > 5; // More than 5 words indicates a sentence
    
    // Call onProceed with the updated analysis and search type
    if (onProceed) {
      onProceed(updatedAnalysis, isSingleSentenceSearch, gender, selectedStream, requireNoVisa);
    }
  };

  // Render loading skeleton
  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center py-10">
      <div className="relative mb-6 w-16 h-16">
        <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
        <div className="absolute top-0 left-0 w-full h-full border-4 border-[#02B491] rounded-full animate-spin-slow border-t-transparent"></div>
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#02B491]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      </div>
      <p className="text-gray-700 text-lg font-medium mb-2">Analysing job description...</p>
      <p className="text-gray-500 text-sm max-w-xs mx-auto text-center">
        Our AI is extracting key requirements from the job description.
      </p>
    </div>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 transition-opacity duration-300" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl shadow-xl w-[800px] max-h-[90vh] overflow-y-auto animate-slideUp" onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <>
            {/* Loading Header */}
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800">Analysing {roleTitle}</h2>
            </div>
            {renderLoading()}
          </>
        ) : (
          <div className={`opacity-0 transition-opacity duration-500 ${isReady ? 'opacity-100' : ''}`}>
            {/* Updated Green Banner Header */}
            <div className="bg-[#f0f9f6] border border-[#e0f2ec] p-6 rounded-t-xl mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center text-[#02B491] font-medium">
                  <EditIcon className="w-5 h-5 mr-2" />
                  <h2 className="text-xl">Tell Us Your Must-Haves & Nice-to-Haves</h2>
                </div>
                {jobDescriptionUrl && (
                  <button
                    onClick={() => window.open(jobDescriptionUrl, '_blank')}
                    className="flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Job Description
                  </button>
                )}
              </div>
              <p className="text-gray-700">
                We used your job description to generate these suggestions. Feel free to remove, add, 
                or modify them. Click on any tag to mark it as a <strong>Must-Have</strong> or 
                <strong> Nice-to-Have</strong>.
              </p>
            </div>
            
            <div className="px-6">
              {/* Gender Filter Section */}
              {showGenderFilter && (
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    Filter by Gender
                  </h3>
                  <div className="relative">
                    <select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#02B491] focus:border-transparent transition-all duration-200 appearance-none"
                    >
                      <option value="any">Any Gender</option>
                      <option value="Man">Men (excludes candidates marked as "Woman")</option>
                      <option value="Woman">Women (excludes candidates marked as "Man")</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l-4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Note: Candidates without gender specified will be included in filtered results.
                  </p>
                </div>
              )}

              {/* Stream Selector Section */}
              {shouldShowStreamSelector && (
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    Select Stream
                  </h3>
                  <div className="relative">
                    <select
                      value={selectedStream}
                      onChange={(e) => setSelectedStream(e.target.value)}
                      className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#02B491] focus:border-transparent transition-all duration-200 appearance-none"
                    >
                      <option value="">Select a stream</option>
                      {streamOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l-4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Note: Candidates without stream specified will be included in filtered results.
                  </p>
                </div>
              )}

              {/* Visa Filter Section */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  Visa Requirements
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requireNoVisa}
                      onChange={(e) => setRequireNoVisa(e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#02B491] border-gray-300 rounded focus:ring-[#02B491] focus:ring-2"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        Only show candidates who don't need visa sponsorship
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Excludes candidates who need sponsorship now or in the future (e.g., Post Study Visa, Skilled Worker Visa)
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Includes: British/Irish passport holders, Settlement Status, ILR, Ancestry Visa, Dependent Visa holders
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Add More Requirements Section */}
              <div className="flex items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-800 mr-3">
                  What else are you looking for in a candidate?
                </h3>
              </div>
              
              {amNote && (
                <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400">
                  <p className="font-bold text-yellow-800">AM has left you this note:</p>
                  <p className="mt-1 text-yellow-700">{amNote}</p>
                </div>
              )}
              
              {/* Simplified Input Section */}
              <div className="mb-5 relative">
                <div className="relative flex items-center w-full bg-gray-100 rounded-lg focus-within:ring-2 focus-within:ring-[#02B491] transition-all duration-200 border-none outline-none">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    className="w-full pl-10 pr-32 py-3 bg-transparent focus:outline-none border-none outline-none resize-none"
                    style={{ overflowY: 'hidden' }}
                    value={newRequirementTag}
                    onChange={(e) => setNewRequirementTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        addTag(newRequirementTag);
                        e.preventDefault();
                      }
                    }}
                    placeholder="Type a skill, qualification or experience..."
                  />
                  {newRequirementTag && (
                    <button
                      onClick={clearInput}
                      className="absolute right-28 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <CloseIcon className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    className="absolute right-0 top-0 h-full px-6 bg-[#02B491] hover:bg-[#029f7d] text-white rounded-r-lg transition-colors duration-200 flex items-center"
                    onClick={() => addTag(newRequirementTag)}
                  >
                    <AddIcon className="mr-1 w-5 h-5" />
                    Add
                  </button>
                </div>
              </div>
              
              {/* Requirements Section with Legend on Right */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-2xl font-bold text-gray-800">Your Current Requirements</h3>
                  
                  {/* Show different legend based on search type */}
                  {requirementTags.length === 1 && requirementTags[0].text.split(' ').length > 5 ? (
                    <div className="flex items-center space-x-2">
                      <StarIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-blue-600 font-medium">AI Sentence Search Mode</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-[#02B491] mr-2"></div>
                        <span className="text-sm">Must have</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-[#ff9800] mr-2"></div>
                        <span className="text-sm">Nice to have</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {requirementTags.length > 0 ? (
                    requirementTags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.text}
                        onClick={() => toggleTagType(index)}
                        onDelete={() => removeTag(index)}
                        sx={{ 
                          margin: '0.25rem', 
                          transition: 'all 0.2s ease',
                          backgroundColor: tag.type === 'must-have' ? '#02B491' : '#ff9800',
                          color: 'white',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                          }
                        }}
                      />
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm p-2 italic">
                      Add or confirm requirements above to find matching candidates
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer actions */}
        <div className="flex justify-end p-6 pt-4 border-t border-gray-200">
          <button 
            onClick={() => onClose('cancel')} 
            className="px-6 py-3 text-gray-600 hover:text-gray-800 mr-4 transition-colors duration-200"
          >
            Cancel
          </button>
          <button 
            onClick={handleProceed} 
            className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${isLoading || requirementTags.length === 0 || (shouldShowStreamSelector && !selectedStream) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#02B491] hover:bg-[#029f7d] text-white'}`}
            disabled={isLoading || requirementTags.length === 0 || (shouldShowStreamSelector && !selectedStream)}
          >
            {requirementTags.length === 1 && requirementTags[0].text.split(' ').length > 5 
              ? 'Find Best Matches with AI' 
              : isEditMode ? 'Re-run Recommendations' : 'Find Matching Candidates'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagReviewModal;
